import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MessageSquare, ArrowLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CreateSupportTicket = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const orderId = searchParams.get('orderId');
  const orderNumber = searchParams.get('orderNumber');
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    category: 'general'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject || !formData.description) {
      toast({
        title: t('createTicket.error.missingInfo'),
        description: t('createTicket.error.missingInfoDescription'),
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const authToken = localStorage.getItem('accessToken');
      
      if (!authToken) {
        toast({
          title: t('createTicket.error.authRequired'),
          description: t('createTicket.error.authDescription'),
          variant: 'destructive'
        });
        navigate('/');
        return;
      }
      
      const response = await axios.post(
        `${API}/support/tickets`,
        {
          orderId: orderId || undefined,  // Send undefined if no orderId (won't be included in request)
          subject: formData.subject,
          description: formData.description,
          priority: formData.priority
        },
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      toast({
        title: t('createTicket.success.title'),
        description: t('createTicket.success.description')
      });

      // Navigate to support tickets list or ticket detail
      navigate('/account/support');
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || t('createTicket.error.createFailed'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-2xl">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-[#be1f1a] mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('createTicket.backToOrder')}
        </button>

        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="flex items-center gap-3 mb-6">
            <MessageSquare className="h-8 w-8 text-[#be1f1a]" />
            <div>
              <h1 className="text-2xl font-bold text-[#2C2420]">{t('createTicket.title')}</h1>
              {orderNumber ? (
                <p className="text-gray-600">{t('createTicket.relatedToOrder')} #{orderNumber}</p>
              ) : (
                <p className="text-gray-600">{t('createTicket.generalSupport')}</p>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="subject">{t('createTicket.subject')} *</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                placeholder={t('createTicket.subjectPlaceholder')}
                required
              />
            </div>

            <div>
              <Label htmlFor="priority">{t('createTicket.priority')}</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({...formData, priority: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{t('createTicket.priorityLow')}</SelectItem>
                  <SelectItem value="medium">{t('createTicket.priorityMedium')}</SelectItem>
                  <SelectItem value="high">{t('createTicket.priorityHigh')}</SelectItem>
                  <SelectItem value="urgent">{t('createTicket.priorityUrgent')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="description">{t('createTicket.description')} *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder={t('createTicket.descriptionPlaceholder')}
                rows={6}
                required
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>{t('createTicket.tip')}:</strong> {t('createTicket.tipText')}
              </p>
            </div>

            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex-1"
              >
                {t('createTicket.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#be1f1a] hover:bg-[#a71b17]"
              >
                {loading ? t('createTicket.creating') : t('createTicket.createButton')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateSupportTicket;